import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import RedDisclaimer from "../components/RedDisclaimer";

export default function PrivacyPolicyPage() {
  return (
    <div
      data-testid="privacy-policy-page"
      className="min-h-screen bg-[#0A0A0A] text-white"
    >
      <header className="border-b border-[#27272A] py-4">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-mono inline-flex items-center gap-2">
            <ShieldCheck size={12} /> LEGAL · PRIVACY_POLICY_AND_OTHER_LEGAL_CONDITIONS
          </div>
          <h1 className="font-mono text-2xl sm:text-3xl mt-1 tracking-tight">
            Privacy Policy &amp; Other Legal Conditions
          </h1>
          <p className="text-[12px] font-mono text-zinc-500 mt-2">
            Last updated: 2026-03-07 · This document consolidates the privacy
            policy, standard SaaS terms, and applicable US / Canadian legal
            conditions for THE_MINDSTEIN.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6 text-[13px] font-mono text-zinc-300 leading-relaxed">
        <RedDisclaimer testid="policy-red-disclaimer" />

        <Section title="1 · Who we are">
          <p>
            <b className="text-white">THE_MINDSTEIN</b> (&quot;we&quot;, &quot;us&quot;,
            &quot;the platform&quot;) is a Canadian-operated software-as-a-service
            product providing AI-assisted research, ideation, and collaborative
            libraries. Contact for privacy / legal matters: {" "}
            <span className="text-emerald-300">privacy@mindstein.app</span>.
          </p>
        </Section>

        <Section title="2 · What data we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li>Account data: email, hashed password, plan, role, PIN (bcrypt-hashed), phone (if 2FA is enabled).</li>
            <li>Usage data: bot prompts and responses, saved library items, uploaded files (encrypted at rest), collaboration workspace activity, IP address, device / user-agent fingerprint, timestamps.</li>
            <li>Billing data: plan selection, top-up/add-on history. <b>Payment card data is handled by Stripe — we never store card numbers.</b></li>
            <li>Compliance data: content-scan refusals (with the offending term censored), quarantined items, security-alert audit trail.</li>
          </ul>
        </Section>

        <Section title="3 · How we use it">
          <ul className="list-disc pl-5 space-y-1">
            <li>To deliver the service you asked for (bot answers, library storage, collab).</li>
            <li>To bill your subscription and honour the 30-day commitment window described in the Terms.</li>
            <li>To secure the platform (detect abuse, unknown-device flags, arms/ITAR content refusals).</li>
            <li>To comply with lawful requests from Canadian and US law-enforcement agencies.</li>
          </ul>
          <p className="mt-2">
            <b className="text-white">We do not sell your data.</b> We do not
            train third-party models on your prompts.
          </p>
        </Section>

        <Section title="4 · Your rights (PIPEDA · GDPR-style · CCPA)">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Access</b> — request a copy of the data we hold about you at any time via <span className="text-emerald-300">privacy@mindstein.app</span>.</li>
            <li><b>Correction</b> — ask us to correct inaccurate personal information.</li>
            <li><b>Deletion</b> — delete your account (Account page) or ask us to purge your data (30 days to complete).</li>
            <li><b>Portability</b> — export your library any time from <Link className="text-emerald-300 underline" to="/library">/library</Link> as a ZIP with markdown + files + manifest.</li>
            <li><b>Withdraw consent</b> — cancel your subscription (30-day honour window applies for paid tiers).</li>
          </ul>
        </Section>

        <Section title="5 · Encryption at rest & PIN lock">
          <p>
            Bot responses, library saves, uploaded files, and Idea-Wall bodies
            are encrypted at rest using Fernet (authenticated AES-128-CBC +
            HMAC-SHA256). Additionally, users may set a 4-6 digit PIN and mark
            individual saves as <b className="text-white">PIN-locked</b>, which
            requires an active 60-second unlock window to view or export the
            content.
          </p>
        </Section>

        <Section title="6 · Cookies & session tokens">
          <p>
            We use one first-party HTTP-only session cookie for authentication.
            We do not use third-party advertising cookies. Analytics, if any,
            are aggregate and non-identifying.
          </p>
        </Section>

        <Section title="7 · Data retention">
          <p>
            Library saves are <b className="text-white">permanent until you delete them</b>.
            Security activity logs (IP, device fingerprint) are retained for 90
            days. Refunded / cancelled subscription records are retained for 7
            years for tax audit compliance.
          </p>
        </Section>

        <Section title="8 · Data location & transfers">
          <p>
            Primary storage is in Canada. Bot inference is served by an LLM
            provider under contract; prompts are transmitted over TLS and are
            not retained by the LLM provider beyond the inference request.
            EU / UK users benefit from the same protections; no cross-border
            transfer occurs to sanctioned jurisdictions.
          </p>
        </Section>

        <Section title="9 · Prohibited use — arms · military · ITAR">
          <p>
            Per section 8 of the Terms of Service, MindStein strictly prohibits
            any use related to arms, weapons, ammunition, ordnance, military
            equipment, defense aerospace systems, or any activity violating US
            export controls (ITAR / EAR / USML) or Canadian export-control laws.
            Attempted prompts are refused by the bot in real time, censored,
            and logged. Repeated attempts result in permanent bans, personal
            liability for damages, and cooperation with law enforcement.
          </p>
        </Section>

        <Section title="10 · Consumer protection (Canada · US)">
          <ul className="list-disc pl-5 space-y-1">
            <li><b>PIPEDA</b> — personal-information protection with breach-notification obligation to the OPC and affected users.</li>
            <li><b>PPCDA / Bill C-27</b> — consent, transparency, and data-rights obligations align with the Consumer Privacy Protection Act.</li>
            <li><b>Consumer Protection Acts (provincial)</b> — no hidden fees, no drip pricing, clear cancellation path.</li>
            <li><b>CAN-SPAM · CASL</b> — we do not send unsolicited email; every transactional email includes an unsubscribe link.</li>
            <li><b>CCPA / CPRA</b> — California residents may opt-out of any &quot;sale&quot; of personal data; we do not sell personal data.</li>
          </ul>
        </Section>

        <Section title="11 · Subscription commitment, refunds, cancellations">
          <p>
            Monthly plans (<span className="text-white">Starter · Elite · Pro · Team</span>) —
            once you use the bot for the first time after activation, your
            subscription is honoured for the current 30-day period; you may
            cancel to prevent renewal any time before the next 30-day cycle.
          </p>
          <p className="mt-2">
            Annual plans (<span className="text-white">Elite Annual · Pro Annual</span>) —
            30-day refund window from the first bot use. If a refund is
            requested in that window, we charge one month at the equivalent
            monthly tier price and refund the balance. Outside the window, the
            annual subscription is honoured to term.
          </p>
          <p className="mt-2 text-zinc-500">
            All refund settlements are executed via Stripe. Please allow 5-10
            business days for the funds to appear in your account.
          </p>
        </Section>

        <Section title="12 · Governing law">
          <p>
            These terms are governed by the laws of Ontario, Canada, and where
            applicable the laws of the state of Delaware, USA. Disputes are
            resolved by arbitration under the ICC rules; nothing in this clause
            waives statutory consumer-protection rights or the right to bring
            an individual small-claims action.
          </p>
        </Section>

        <Section title="13 · Changes to this policy">
          <p>
            We publish changes to this policy at least 14 days before they take
            effect. Material changes are additionally sent by email to every
            active account.
          </p>
        </Section>

        <div className="border-t border-[#27272A] pt-4 mt-6">
          <Link
            to="/legal"
            className="text-[11px] font-mono text-zinc-400 hover:text-white uppercase tracking-wider"
          >
            ← BACK_TO_TERMS
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  const anchor = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <section id={anchor}>
      <h2 className="text-[15px] font-mono text-white tracking-tight mb-2">
        {title}
      </h2>
      <div className="text-zinc-400">{children}</div>
    </section>
  );
}
